import React from 'react';
import { 
  Scale, Anchor, Car, Shield, AlertTriangle, Heart, 
  Lightbulb, Quote, CheckCircle, AlertCircle, Info,
  DollarSign, Clock, FileText, Phone
} from 'lucide-react';

// Category icons and colors for visual consistency - using site's dusty rose theme (#D5A4AF)
export const categoryConfig = {
  'Personal Injury': {
    icon: Scale,
    gradient: 'from-primary to-coral-500',
    bgLight: 'bg-coral-50',
    textColor: 'text-primary',
    borderColor: 'border-coral-200'
  },
  'Maritime Law': {
    icon: Anchor,
    gradient: 'from-ocean-500 to-ocean-700',
    bgLight: 'bg-ocean-50',
    textColor: 'text-ocean-600',
    borderColor: 'border-ocean-200'
  },
  'Car Accidents': {
    icon: Car,
    gradient: 'from-primary to-coral-500',
    bgLight: 'bg-coral-50',
    textColor: 'text-primary',
    borderColor: 'border-coral-200'
  },
  'First Responders': {
    icon: Shield,
    gradient: 'from-ocean-500 to-ocean-700',
    bgLight: 'bg-ocean-50',
    textColor: 'text-ocean-600',
    borderColor: 'border-ocean-200'
  },
  'Wrongful Death': {
    icon: Heart,
    gradient: 'from-primary to-coral-500',
    bgLight: 'bg-coral-50',
    textColor: 'text-primary',
    borderColor: 'border-coral-200'
  },
  'Slip and Fall': {
    icon: AlertTriangle,
    gradient: 'from-primary to-coral-500',
    bgLight: 'bg-coral-50',
    textColor: 'text-primary',
    borderColor: 'border-coral-200'
  },
  'default': {
    icon: Scale,
    gradient: 'from-primary to-coral-500',
    bgLight: 'bg-coral-50',
    textColor: 'text-primary',
    borderColor: 'border-coral-200'
  }
};

// Get category config with fallback
export const getCategoryConfig = (category) => {
  return categoryConfig[category] || categoryConfig['default'];
};

// Pull Quote Component - for highlighting important statements
export const PullQuote = ({ children, author }) => (
  <div className="my-8 relative">
    <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-primary to-coral-500 rounded-full" />
    <blockquote className="pl-8 pr-4 py-4 bg-gradient-to-r from-coral-50 to-transparent rounded-r-xl">
      <Quote className="w-8 h-8 text-primary/30 mb-2" />
      <p className="text-xl md:text-2xl font-heading text-secondary italic leading-relaxed">
        "{children}"
      </p>
      {author && (
        <cite className="block mt-3 text-slate-500 font-medium not-italic">
          — {author}
        </cite>
      )}
    </blockquote>
  </div>
);

// Key Takeaway Box - for summarizing important points
export const KeyTakeaway = ({ title = "Key Takeaway", children }) => (
  <div className="my-8 bg-gradient-to-br from-coral-50 to-coral-100/50 border border-coral-200 rounded-2xl p-6 shadow-sm">
    <div className="flex items-center gap-3 mb-4">
      <div className="p-2 bg-primary rounded-xl">
        <Lightbulb className="w-5 h-5 text-white" />
      </div>
      <h4 className="font-heading text-lg font-bold text-secondary">{title}</h4>
    </div>
    <div className="text-secondary/80 leading-relaxed">
      {children}
    </div>
  </div>
);

// Warning/Important Box
export const WarningBox = ({ title = "Important", children }) => (
  <div className="my-8 bg-gradient-to-br from-amber-50 to-amber-100/50 border border-amber-200 rounded-2xl p-6 shadow-sm">
    <div className="flex items-center gap-3 mb-4">
      <div className="p-2 bg-amber-500 rounded-xl">
        <AlertCircle className="w-5 h-5 text-white" />
      </div>
      <h4 className="font-heading text-lg font-bold text-amber-800">{title}</h4>
    </div>
    <div className="text-amber-900 leading-relaxed">
      {children}
    </div>
  </div>
);

// Info Box
export const InfoBox = ({ title = "Did You Know?", children }) => (
  <div className="my-8 bg-gradient-to-br from-blue-50 to-blue-100/50 border border-blue-200 rounded-2xl p-6 shadow-sm">
    <div className="flex items-center gap-3 mb-4">
      <div className="p-2 bg-blue-500 rounded-xl">
        <Info className="w-5 h-5 text-white" />
      </div>
      <h4 className="font-heading text-lg font-bold text-blue-800">{title}</h4>
    </div>
    <div className="text-blue-900 leading-relaxed">
      {children}
    </div>
  </div>
);

// Stat Highlight Component
export const StatHighlight = ({ stats }) => (
  <div className="my-10 grid grid-cols-2 md:grid-cols-4 gap-4">
    {stats.map((stat, index) => (
      <div 
        key={index}
        className="bg-gradient-to-br from-secondary to-slate-800 text-white rounded-2xl p-5 text-center shadow-lg"
      >
        <div className="text-3xl md:text-4xl font-bold text-primary mb-1">
          {stat.value}
        </div>
        <div className="text-sm text-slate-300">
          {stat.label}
        </div>
      </div>
    ))}
  </div>
);

// Checklist Component
export const Checklist = ({ title, items }) => (
  <div className="my-8 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
    {title && (
      <h4 className="font-heading text-lg font-bold text-secondary mb-4 flex items-center gap-2">
        <FileText className="w-5 h-5 text-primary" />
        {title}
      </h4>
    )}
    <ul className="space-y-3">
      {items.map((item, index) => (
        <li key={index} className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
          <span className="text-slate-700">{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

// Timeline/Steps Component
export const StepGuide = ({ title, steps }) => (
  <div className="my-10">
    {title && (
      <h3 className="font-heading text-2xl font-bold text-secondary mb-6 text-center">
        {title}
      </h3>
    )}
    <div className="relative">
      {/* Vertical line */}
      <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-coral-300 to-primary hidden md:block" />
      
      <div className="space-y-6">
        {steps.map((step, index) => (
          <div key={index} className="flex gap-4 md:gap-6">
            <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-primary to-coral-500 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg z-10">
              {index + 1}
            </div>
            <div className="flex-1 bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
              <h4 className="font-heading text-lg font-semibold text-secondary mb-2">
                {step.title}
              </h4>
              <p className="text-slate-600">
                {step.description}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Quick Contact CTA inline
export const InlineContactCTA = () => (
  <div className="my-8 bg-gradient-to-r from-coral-500 to-primary rounded-2xl p-6 text-white shadow-lg">
    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        <div className="p-3 bg-white/20 rounded-full">
          <Phone className="w-6 h-6" />
        </div>
        <div>
          <p className="font-heading font-bold text-lg">Have questions about your case?</p>
          <p className="text-coral-100">Free consultation • No fee unless you win</p>
        </div>
      </div>
      <a 
        href="tel:425-675-1224"
        className="bg-white text-coral-600 px-6 py-3 rounded-full font-bold hover:bg-coral-50 transition-all shadow-md whitespace-nowrap"
      >
        Call (425) 675-1224
      </a>
    </div>
  </div>
);

// Category Badge with icon
export const CategoryBadge = ({ category, size = 'md' }) => {
  const config = getCategoryConfig(category);
  const Icon = config.icon;
  
  const sizeClasses = {
    sm: 'px-3 py-1 text-xs gap-1',
    md: 'px-4 py-2 text-sm gap-2',
    lg: 'px-5 py-2.5 text-base gap-2'
  };
  
  return (
    <span className={`inline-flex items-center ${sizeClasses[size]} ${config.bgLight} ${config.textColor} rounded-full font-medium`}>
      <Icon className={size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} />
      {category}
    </span>
  );
};

// Reading Progress Indicator
export const ReadingProgress = ({ progress }) => (
  <div className="fixed top-0 left-0 right-0 h-1 bg-slate-200 z-50">
    <div 
      className="h-full bg-gradient-to-r from-primary to-coral-500 transition-all duration-150"
      style={{ width: `${progress}%` }}
    />
  </div>
);

// Bottom Line Summary Box - for article conclusions
export const BottomLine = ({ children }) => (
  <div className="my-10 bg-gradient-to-br from-secondary to-slate-800 rounded-2xl p-8 shadow-xl">
    <div className="flex items-center gap-3 mb-4">
      <div className="p-2 bg-primary rounded-xl">
        <CheckCircle className="w-6 h-6 text-white" />
      </div>
      <h4 className="font-heading text-xl font-bold text-white">The Bottom Line</h4>
    </div>
    <div className="text-slate-200 leading-relaxed text-lg">
      {children}
    </div>
  </div>
);

// Case Citation Callout - for legal case references
export const CaseCitation = ({ caseName, year, court, children }) => (
  <div className="my-6 bg-slate-50 border-l-4 border-slate-400 rounded-r-xl p-5">
    <div className="flex items-start gap-3">
      <Scale className="w-5 h-5 text-slate-500 mt-1 flex-shrink-0" />
      <div>
        <p className="font-semibold text-secondary">
          <em>{caseName}</em> {year && `(${year})`}
          {court && <span className="text-slate-500 font-normal"> — {court}</span>}
        </p>
        {children && (
          <p className="text-slate-600 mt-2 italic">"{children}"</p>
        )}
      </div>
    </div>
  </div>
);

// Why This Matters Grid - icon grid for key points
export const WhyThisMattersGrid = ({ title = "Why This Matters", items }) => (
  <div className="my-8 p-6 bg-coral-50 rounded-2xl border-2 border-coral-200">
    <h4 className="text-center font-heading font-bold text-secondary text-xl mb-6">{title}</h4>
    <div className="grid md:grid-cols-3 gap-4">
      {items.map((item, idx) => (
        <div key={idx} className="bg-white rounded-xl p-5 text-center shadow-sm">
          <div className="text-3xl mb-3">{item.icon}</div>
          <p className="font-bold text-secondary mb-2">{item.title}</p>
          <p className="text-sm text-slate-600">{item.desc}</p>
        </div>
      ))}
    </div>
  </div>
);

export default {
  PullQuote,
  KeyTakeaway,
  WarningBox,
  InfoBox,
  StatHighlight,
  Checklist,
  StepGuide,
  InlineContactCTA,
  CategoryBadge,
  ReadingProgress,
  BottomLine,
  CaseCitation,
  WhyThisMattersGrid,
  getCategoryConfig
};
